/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useCombobox } from "downshift";
import { navigate } from "gatsby";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { Icon } from "../../Icons";
import { Loader } from "../../Loader";
import * as styles from "./HotelSearchBoxTypeahead.module.scss";

const defaultLabels = {
	noResults: "No results found",
};

const HotelSearchBoxTypeahead = React.forwardRef(
	(
		{
			id,
			loading,
			data,
			selectedItem,
			searchLabel,
			searchHotelsLabel,
			// isFocused,
			getIconRef,
			hotelUrl,
			environment,
			children,
			className,
			onFocus,
			onBlur,
			onChange,
			onInput,
			labels: providedLabels,
		},
		ref
	) => {
		const labels = { ...defaultLabels, ...providedLabels };
		const [inputItems, setInputItems] = useState(data);
		const dropdownReference = useRef(null);

		const itemToString = item => (item ? item.label : "");

		const getDownshiftList = list =>
			list
				? list.reduce((result, section) => {
						result.push(...section.items);
						return result;
				  }, [])
				: list;

		const getItems = useCallback(
			inputValue => {
				if (!inputValue?.trim() || inputValue?.trim() === "") return data;
				return data
					.map(option => ({
						title: option.title,
						items: option.items.filter(
							o =>
								o?.label?.toLowerCase().includes(inputValue?.toLowerCase()) ||
								o?.value?.toLowerCase().includes(inputValue?.toLowerCase())
						),
					}))
					.filter(f => f.items.length > 0);
			},
			[data]
		);

		const stateReducer = (state, actionAndChanges) => {
			const { type, changes } = actionAndChanges;

			if (type === useCombobox.stateChangeTypes.InputKeyDownEscape) {
				return {
					...changes,
					selectedItem: null,
				};
			}
			if (type === useCombobox.stateChangeTypes.InputBlur) {
				return {
					...changes,
					inputValue: itemToString(changes.selectedItem),
				};
			}
			return changes;
		};

		const {
			isOpen,
			selectItem,
			inputValue,
			highlightedIndex,
			getMenuProps,
			getInputProps,
			getComboboxProps,
			getItemProps,
		} = useCombobox({
			id,
			labelId: id,
			stateReducer,
			items: getDownshiftList(inputItems),
			itemToString,
			onStateChange: changes => {
				if (changes.type === useCombobox.stateChangeTypes.InputBlur) {
					onBlur();
				}
				if (changes.inputValue && changes.inputValue === changes.selectedItem?.label) {
					setInputItems(data);
				}
			},
			onIsOpenChange: changes => {
				const { matches: isMobile } = window.matchMedia("(max-width: 576px)");

				if (changes.isOpen && isMobile && dropdownReference?.current) {
					disableBodyScroll(dropdownReference.current);
					window.scrollTo({
						top:
							dropdownReference.current.parentElement.getBoundingClientRect().top -
							16 +
							window.scrollY,
						behavior: "smooth",
					});
				} else {
					clearAllBodyScrollLocks();
				}
			},
			onInputValueChange: changes => {
				onInput(changes.inputValue);
				setInputItems(getItems(changes.inputValue));
			},
			onSelectedItemChange: changes => {
				onChange(changes.selectedItem);
			},
			environment: {
				addEventListener: environment.addEventListener.bind(environment),
				removeEventListener: environment.removeEventListener.bind(environment),
				document: environment.ownerDocument || document,
			},
		});

		const renderList = list =>
			list.reduce(
				(result, section, sectionIndex) => {
					result.sections.push(
						<ul
							// eslint-disable-next-line react/no-array-index-key
							key={sectionIndex}
							role="group"
							aria-labelledby={`${id}-group-${sectionIndex}`}
							className={styles.list}>
							{section.items.map(item => {
								// eslint-disable-next-line no-param-reassign
								result.itemIndex += 1;
								return (
									<li
										{...getItemProps({
											key: result.itemIndex,
											item,
											index: result.itemIndex,
											className: cx(
												styles.item,
												JSON.stringify(selectedItem) === JSON.stringify(item) && styles.selected,
												highlightedIndex === result.itemIndex && styles.highlighted
											),
										})}>
										{itemToString(item)}
									</li>
								);
							})}
						</ul>
					);

					return result;
				},
				{ sections: [], itemIndex: -1 }
			).sections;

		useEffect(() => {
			if (data) {
				setInputItems(getItems(inputValue));
			}
		}, [data, getItems, inputValue]);

		useEffect(() => {
			selectItem(selectedItem);
		}, [selectItem, selectedItem]);

		const handleIconClick = e => {
			e.stopPropagation();

			navigate(hotelUrl);
		};

		return (
			<>
				<div
					{...getComboboxProps({
						ref,
						className: cx(
							className,
							styles.typeahead,
							isOpen && styles.open
							// isFocused && styles.focused
						),
					})}>
					<div className={styles.inputAndButtonWrapper}>
						<label htmlFor="hotel-search" className={styles.srOnly}>
							{searchHotelsLabel}
						</label>
						<input
							{...getInputProps({
								id: "hotel-search",
								className: cx(styles.input, isOpen && styles.open),
								placeholder: loading ? "" : searchHotelsLabel,
								onFocus,
								onBlur,
								onKeyUp: event => {
									if (hotelUrl !== "" && event.keyCode === 13) {
										event.stopPropagation();
										navigate(hotelUrl);
									}
								},
							})}
						/>
						{!loading && (
							<button
								ref={getIconRef}
								type="button"
								className={cx(styles.icon, isOpen && styles.open)}
								onClick={handleIconClick}
								aria-label={searchLabel}>
								<Icon name="search" />
							</button>
						)}
						{loading && <Loader className={styles.loader} />}
					</div>
					<div
						{...getMenuProps({
							ref: dropdownReference,
						})}
						className={cx(styles.listbox, isOpen && styles.open)}>
						{inputItems.length
							? renderList(inputItems)
							: !loading && (
									// eslint-disable-next-line react/jsx-indent
									<span
										role="option"
										aria-selected="true"
										aria-disabled="true"
										className={styles.noResults}>
										{labels.noResults}
									</span>
							  )}
					</div>
				</div>
				{children}
			</>
		);
	}
);
const docType = typeof document === "undefined" ? Function : document;
HotelSearchBoxTypeahead.propTypes = {
	id: PropTypes.string.isRequired,
	loading: PropTypes.bool,
	searchLabel: PropTypes.string.isRequired,
	searchHotelsLabel: PropTypes.string.isRequired,
	data: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			items: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					value: PropTypes.string,
				})
			),
		})
	).isRequired,
	selectedItem: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	}),
	isFocused: PropTypes.bool,
	getIconRef: PropTypes.object,
	hotelUrl: PropTypes.string,
	environment: PropTypes.any,
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
	className: PropTypes.string,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onInput: PropTypes.func,
	labels: PropTypes.shape({
		noResults: PropTypes.string,
	}),
};

HotelSearchBoxTypeahead.defaultProps = {
	loading: false,
	selectedItem: null,
	isFocused: false,
	getIconRef: { current: undefined },
	hotelUrl: "",
	environment: docType,
	children: undefined,
	className: undefined,
	onFocus: () => {},
	onBlur: () => {},
	onChange: () => {},
	onInput: () => {},
	labels: defaultLabels,
};

export default HotelSearchBoxTypeahead;
export { HotelSearchBoxTypeahead };
